import { UmpireStrikeZoneChartConfigDto, UmpireStrikeZoneChartDataDto } from 'types/CodegenReportTypes';
import { LegendItem } from 'components/Legend/Legend';

import { ChartProps } from 'utilities/chartIdToComponent';
import {
  umpireCallToColor,
  umpireCallToTextColor,
  umpireCallCorrectnessToColor,
  umpireCallCorrectnessToTextColor,
} from 'utilities/colorHelpers';
import { umpireCallOptions, umpireCallCorrectnessOptions } from 'utilities/constants';
import PitchLocationChart from 'components/PitchLocationChart/PitchLocationChart';
import useUmpireSingleSessionReportStore from 'hooks/UmpireReport/SingleSession/reportStore';
import useUmpireSessionReportStore from 'hooks/UmpireReport/AllSessions/reportStore';
import { ReportRoutes } from 'Routes';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

const extraLegendItems: LegendItem[] = [
  { label: 'Zone without buffers', icon: <div style={{ width: '14px', border: '2px solid #414141' }}></div> },
  { label: 'Zone with buffers', icon: <div style={{ width: '14px', border: '2px dashed #414141' }}></div> },
];

const UmpireStrikeZoneChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<UmpireStrikeZoneChartDataDto, UmpireStrikeZoneChartConfigDto>) => {
  const { pathname } = useLocation();

  const singleSessionStore = useUmpireSingleSessionReportStore();
  const allSessionsStore = useUmpireSessionReportStore();

  const { showBufferZone, enablePitchSelection } = useMemo(() => {
    const isSingle = pathname.includes(ReportRoutes.UmpireReport.SingleSession);
    const isAll = pathname.includes(ReportRoutes.UmpireReport.AllSessions);

    let buffer = false;
    let selection = false;

    if (isSingle) {
      buffer = singleSessionStore.selectedConfgValues.strikeZoneDisplay === 'WithBuffers';
      selection = true;
    } else if (isAll) {
      buffer = allSessionsStore.selectedConfgValues.strikeZoneDisplay === 'WithBuffers';
      selection = false;
    }

    return { showBufferZone: buffer, enablePitchSelection: selection };
  }, [pathname, singleSessionStore.selectedConfgValues.strikeZoneDisplay, allSessionsStore.selectedConfgValues.strikeZoneDisplay]);

  const groupLabelsToSimulate = useMemo(
    () => (extraChartData.umpirePitchGrouping === 'Call' ? umpireCallOptions : umpireCallCorrectnessOptions),
    [extraChartData.umpirePitchGrouping]
  );

  const getGroupLabelColor = useCallback(
    (groupLabel: string) => {
      return extraChartData.umpirePitchGrouping === 'Call' ? umpireCallToColor[groupLabel] : umpireCallCorrectnessToColor[groupLabel];
    },
    [extraChartData.umpirePitchGrouping]
  );

  const getGroupLabelTextColor = useCallback(
    (groupLabel: string) => {
      return extraChartData.umpirePitchGrouping === 'Call'
        ? umpireCallToTextColor[groupLabel]
        : umpireCallCorrectnessToTextColor[groupLabel];
    },
    [extraChartData.umpirePitchGrouping]
  );

  return (
    <PitchLocationChart
      chartData={chartData?.datasets}
      basePlateOrientation={'BatterSide'}
      showBufferZone={showBufferZone}
      groupLabelsToSimulate={groupLabelsToSimulate}
      getGroupLabelColor={getGroupLabelColor}
      getGroupLabelTextColor={getGroupLabelTextColor}
      extraLegendItems={showBufferZone ? extraLegendItems : undefined}
      isLoading={isLoading}
      enablePitchSelection={enablePitchSelection}
    />
  );
};

export default UmpireStrikeZoneChart;
